import { useState } from "react";
import { trpc } from "../lib/trpc";
import {
  MessageSquare, Plus, Trash2, Edit3, Save, X,
  ToggleLeft, ToggleRight,
  Bot, Mail, Phone, Hash,
  CheckCircle2, AlertCircle, Wifi, WifiOff,
  ExternalLink,
} from "lucide-react";

const CHANNEL_CONFIG: Record<string, { label: string; icon: typeof Bot; color: string; bgColor: string; fields: { key: string; label: string; type: string; placeholder: string }[] }> = {
  telegram: {
    label: "Telegram",
    icon: Bot,
    color: "text-blue-500",
    bgColor: "bg-blue-500/10 border-blue-500/20",
    fields: [
      { key: "botToken", label: "Bot Token", type: "password", placeholder: "123456:ABC-DEF..." },
    ],
  },
  whatsapp: {
    label: "WhatsApp",
    icon: Phone,
    color: "text-green-500",
    bgColor: "bg-green-500/10 border-green-500/20",
    fields: [
      { key: "accessToken", label: "Access Token", type: "password", placeholder: "EAAx..." },
      { key: "phoneNumberId", label: "Phone Number ID", type: "text", placeholder: "123456789" },
      { key: "verifyToken", label: "Verify Token", type: "text", placeholder: "my-verify-token" },
    ],
  },
  email: {
    label: "Email",
    icon: Mail,
    color: "text-purple-500",
    bgColor: "bg-purple-500/10 border-purple-500/20",
    fields: [
      { key: "host", label: "SMTP Host", type: "text", placeholder: "smtp.gmail.com" },
      { key: "port", label: "SMTP Port", type: "number", placeholder: "587" },
      { key: "user", label: "Usuário", type: "text", placeholder: "user@domain.com" },
      { key: "pass", label: "Senha", type: "password", placeholder: "app-password" },
      { key: "from", label: "From", type: "text", placeholder: "Megan AI <noreply@domain.com>" },
    ],
  },
};

export function Channels() {
  const [showCreate, setShowCreate] = useState(false);
  const [selectedType, setSelectedType] = useState<string>("telegram");
  const [formName, setFormName] = useState("");
  const [formConfig, setFormConfig] = useState<Record<string, string>>({});
  const [editId, setEditId] = useState<string | null>(null);

  const utils = trpc.useUtils();
  const { data: connections, isLoading: loadingConns } = trpc.channels.listConnections.useQuery();
  const { data: stats } = trpc.channels.getStats.useQuery();

  const createMut = trpc.channels.createConnection.useMutation({
    onSuccess: () => { utils.channels.invalidate(); setShowCreate(false); resetForm(); },
  });
  const updateMut = trpc.channels.updateConnection.useMutation({
    onSuccess: () => { utils.channels.invalidate(); setEditId(null); resetForm(); },
  });
  const toggleMut = trpc.channels.toggleConnection.useMutation({
    onSuccess: () => utils.channels.invalidate(),
  });
  const deleteMut = trpc.channels.deleteConnection.useMutation({
    onSuccess: () => utils.channels.invalidate(),
  });

  function resetForm() {
    setFormName("");
    setFormConfig({});
    setSelectedType("telegram");
  }

  function handleCreate() {
    createMut.mutate({
      channelType: selectedType as "telegram" | "whatsapp" | "email",
      name: formName,
      config: formConfig,
    });
  }

  function handleUpdate() {
    if (!editId) return;
    updateMut.mutate({ id: editId, name: formName, config: formConfig });
  }

  const activeConns = connections?.filter((c: any) => c.status === "active").length || 0;
  const totalConns = connections?.length || 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2.5">
            <MessageSquare className="w-6 h-6 text-primary" />
            Canais
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Configure as conexões de Telegram, WhatsApp e Email da Megan AI
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
        >
          <Plus className="w-4 h-4" /> Novo Canal
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="stats-card bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-secondary rounded-lg">
              <MessageSquare className="w-5 h-5 text-muted-foreground" />
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Total de Canais</div>
              <div className="text-2xl font-bold text-foreground">{totalConns}</div>
            </div>
          </div>
        </div>
        <div className="stats-card bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-emerald-500/10 rounded-lg">
              <Wifi className="w-5 h-5 text-emerald-500" />
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Ativos</div>
              <div className="text-2xl font-bold text-emerald-500">{activeConns}</div>
            </div>
          </div>
        </div>
        <div className="stats-card bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-500/10 rounded-lg">
              <Mail className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Mensagens Processadas</div>
              <div className="text-2xl font-bold text-blue-500">{stats?.totalMessages || 0}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Create/Edit Form */}
      {(showCreate || editId) && (
        <div className="bg-card border border-border rounded-xl p-6 space-y-4">
          <h3 className="text-lg font-bold text-foreground">
            {editId ? "Editar Canal" : "Novo Canal"}
          </h3>
          {!editId && (
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Tipo de Canal</label>
              <div className="flex gap-2">
                {Object.entries(CHANNEL_CONFIG).map(([key, cfg]) => (
                  <button
                    key={key}
                    onClick={() => { setSelectedType(key); setFormConfig({}); }}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm transition-colors ${
                      selectedType === key
                        ? "border-primary bg-primary/10 text-foreground font-medium"
                        : "border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
                    }`}
                  >
                    <cfg.icon className={`w-4 h-4 ${cfg.color}`} />
                    {cfg.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-foreground mb-1.5 block">Nome</label>
            <input
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="Ex: Bot Principal"
              className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
          {CHANNEL_CONFIG[selectedType]?.fields.map((field) => (
            <div key={field.key}>
              <label className="text-sm font-medium text-foreground mb-1.5 block">{field.label}</label>
              <input
                type={field.type}
                value={formConfig[field.key] || ""}
                onChange={(e) => setFormConfig({ ...formConfig, [field.key]: e.target.value })}
                placeholder={field.placeholder}
                className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
          ))}
          <div className="flex items-center gap-3 pt-2">
            <button
              onClick={() => { setShowCreate(false); setEditId(null); resetForm(); }}
              className="px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={editId ? handleUpdate : handleCreate}
              disabled={createMut.isPending || updateMut.isPending}
              className="flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {createMut.isPending || updateMut.isPending ? "Salvando..." : "Salvar"}
            </button>
          </div>
        </div>
      )}

      {/* Connections List */}
      <div className="space-y-3">
        {loadingConns ? (
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <div key={i} className="bg-card border border-border rounded-xl p-4 animate-pulse">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-muted rounded-lg" />
                  <div className="flex-1">
                    <div className="h-4 bg-muted rounded w-1/4 mb-2" />
                    <div className="h-3 bg-muted rounded w-1/3" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : !connections?.length ? (
          <div className="bg-card border border-border rounded-xl p-12 text-center">
            <WifiOff className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground text-sm font-medium">Nenhum canal configurado</p>
            <p className="text-muted-foreground/60 text-xs mt-1 max-w-md mx-auto">
              Conecte Telegram, WhatsApp ou Email para que a Megan AI possa receber e responder mensagens automaticamente.
            </p>
            <button
              onClick={() => setShowCreate(true)}
              className="mt-4 flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium mx-auto hover:opacity-90 transition-opacity"
            >
              <Plus className="w-4 h-4" /> Configurar Primeiro Canal
            </button>
          </div>
        ) : (
          connections.map((conn: any) => {
            const cfg = CHANNEL_CONFIG[conn.channelType];
            const Icon = cfg?.icon || Hash;
            return (
              <div
                key={conn.id}
                className="bg-card border border-border rounded-xl p-4 hover:border-primary/20 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2.5 rounded-lg border ${cfg?.bgColor || "bg-secondary border-border"}`}>
                      <Icon className={`w-5 h-5 ${cfg?.color || "text-muted-foreground"}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-semibold text-foreground">{conn.name}</span>
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                          conn.status === "active"
                            ? "bg-emerald-500/10 text-emerald-500"
                            : "bg-muted text-muted-foreground"
                        }`}>
                          {conn.status === "active" ? (
                            <><CheckCircle2 className="w-2.5 h-2.5" /> Ativo</>
                          ) : (
                            <><AlertCircle className="w-2.5 h-2.5" /> Inativo</>
                          )}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{cfg?.label || conn.channelType}</span>
                        {conn.messageCount > 0 && (
                          <>
                            <span className="text-border">|</span>
                            <span>{conn.messageCount} mensagens</span>
                          </>
                        )}
                        {conn.lastMessageAt && (
                          <>
                            <span className="text-border">|</span>
                            <span>
                              Última: {new Date(conn.lastMessageAt).toLocaleString("pt-BR")}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => toggleMut.mutate({ id: conn.id })}
                      className={`p-2 rounded-lg transition-colors ${
                        conn.status === "active"
                          ? "text-emerald-500 hover:bg-emerald-500/10"
                          : "text-muted-foreground hover:bg-muted"
                      }`}
                      title={conn.status === "active" ? "Desativar" : "Ativar"}
                    >
                      {conn.status === "active" ? (
                        <ToggleRight className="w-5 h-5" />
                      ) : (
                        <ToggleLeft className="w-5 h-5" />
                      )}
                    </button>
                    <button
                      onClick={() => { setEditId(conn.id); setFormName(conn.name); setSelectedType(conn.channelType); }}
                      className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      title="Editar"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => { if (confirm("Excluir este canal?")) deleteMut.mutate({ id: conn.id }); }}
                      className="p-2 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors"
                      title="Excluir"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Info banner */}
      <div className="bg-card border border-dashed border-border rounded-xl p-4 flex items-center gap-3">
        <ExternalLink className="w-5 h-5 text-primary flex-shrink-0" />
        <p className="text-sm text-muted-foreground">
          O histórico unificado de todas as conversas está disponível na página{" "}
          <a href="/brain" className="text-primary hover:opacity-80 font-medium transition-opacity">
            Brain Office
          </a>
        </p>
      </div>
    </div>
  );
}
